import React, { useState, useEffect, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Typography, Paper, Box } from '@mui/material';
import { useAuth } from '../hooks/useAuth';

const navy = '#1A3A6B';

// ── Membership table ──────────────────────────────────────────────────────────
const MEM_COLUMNS = [
  { id: 'membershipType', label: 'Membership Type' },
  { id: 'bvYear',         label: 'Balavihar Year' },
  { id: 'status',         label: 'Status' },
  { id: 'totalAmount',    label: 'Total Amount' },
  { id: 'startDate',      label: 'Start Date' },
  { id: 'endDate',        label: 'End Date' },
  { id: 'createdAt',      label: 'Transaction Date' },
];

function getMemField(row, key) {
  switch (key) {
    case 'membershipType': return row.membershipTypesEntity.name;
    case 'bvYear':         return row.bvYear;
    case 'status':         return row.status;
    case 'totalAmount':    return row.transactionEntity.totalAmount;
    case 'startDate':      return row.startDate;
    case 'endDate':        return row.endDate;
    case 'createdAt':      return row.createdAt;
    default:               return '';
  }
}

// ── Session table ─────────────────────────────────────────────────────────────
const SES_COLUMNS = [
  { id: 'sessionName', label: 'Session Name' },
  { id: 'bvYear',      label: 'Balavihar Year' },
  { id: 'studentName', label: 'Student Name' },
  { id: 'grade',       label: 'Grade' },
  { id: 'status',      label: 'Status' },
  { id: 'startDate',   label: 'Session Start Date' },
  { id: 'endDate',     label: 'Session End Date' },
];

function getSesField(row, key) {
  switch (key) {
    case 'sessionName': return row.sessionEntity.sessionName;
    case 'bvYear':      return row.bvYear;
    case 'studentName': return row.memberEntity.fullName;
    case 'grade':       return row.gradeEntity.grade;
    case 'status':      return row.status;
    case 'startDate':   return row.regStartDate;
    case 'endDate':     return row.regEndDate;
    default:            return '';
  }
}

function descendingComparator(a, b, orderBy, getField) {
  const aVal = getField(a, orderBy);
  const bVal = getField(b, orderBy);
  if (bVal < aVal) return -1;
  if (bVal > aVal) return 1;
  return 0;
}

function SortableTable({ title, rows, columns, getField, rowKey, defaultOrderBy }) {
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState(defaultOrderBy ?? columns[0].id);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (tableContainerRef.current) tableContainerRef.current.scrollLeft = 0;
  }, [rows]);

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
    setPage(0);
  };

  const sortedRows = [...rows].sort((a, b) =>
    order === 'desc'
      ? descendingComparator(a, b, orderBy, getField)
      : -descendingComparator(a, b, orderBy, getField)
  );

  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper elevation={3} sx={{ overflow: 'hidden', mb: 4 }}>
      <Box sx={{ bgcolor: navy, px: 3, py: 2 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', fontFamily: 'Georgia, serif' }}>
          {title}
        </Typography>
      </Box>

      <TableContainer ref={tableContainerRef} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 650 }} aria-label={title}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#e4eaf0' }}>
              {columns.map((col) => (
                <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}
                  sx={{ fontWeight: 700, color: navy, whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                    sx={{
                      '&.Mui-active': { color: navy },
                      '& .MuiTableSortLabel-icon': { color: `${navy} !important` },
                    }}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#888' }}>
                  No records found.
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row, idx) => (
                <TableRow key={rowKey(row) ?? idx}
                  sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' }, '&:hover': { bgcolor: '#f0f4f8' } }}>
                  {columns.map((col) => (
                    <TableCell key={col.id}>{getField(row, col.id)}</TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Paper>
  );
}

export default function BalaviharHistory() {
  const { accessToken, signOut } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const [bvmembershipHistory, setMembershipHistory] = useState([]);
  const [bvsessionHistory, setBvsessionHistory] = useState([]);

  useEffect(() => {
    if (accessToken) BalaviharMembershipHistoryFetch();
  }, [accessToken]);

  const handleLogout = () => { signOut(); };

  const BalaviharMembershipHistoryFetch = async () => {
    try {
      const response = await fetch(`${API_URL}/gquery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-cmdfw-jwt-token': `${accessToken}`,
        },
        body: JSON.stringify({
          query: `
            query {
              bvHistory {
                memberships {
                  membershipId startDate endDate status notes bvYear createdAt createdBy
                  membershipTypesEntity { membershipTypeId name startDate endDate }
                  transactionEntity {
                    transactionId
                    eventTypesEntity { eventTypeId name eventYear }
                    totalAmount paymentStatus createdAt createdBy
                  }
                }
                registeredSessions {
                  bvRegistrationId regStartDate regEndDate bvYear status
                  memberEntity { firstName lastName fullName }
                  sessionEntity { sessionId center sessionName }
                  gradeEntity { gradeId grade }
                }
              }
            }
          `,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setMembershipHistory(data.data.bvHistory.memberships);
        setBvsessionHistory(data.data.bvHistory.registeredSessions);
      } else if (response.status === 403) {
        handleLogout();
      }
    } catch (error) {
      console.error('Error fetching Balavihar History:', error);
    }
  };

  return (
    <Box>
      <SortableTable
        title="Balavihar Membership History"
        rows={bvmembershipHistory}
        columns={MEM_COLUMNS}
        getField={getMemField}
        rowKey={(row) => row.membershipId}
        defaultOrderBy="createdAt"
      />
      <SortableTable
        title="Balavihar Session History"
        rows={bvsessionHistory}
        columns={SES_COLUMNS}
        getField={getSesField}
        rowKey={(row) => row.bvRegistrationId}
        defaultOrderBy="startDate"
      />
    </Box>
  );
}
